import React from 'react';
import { observer } from 'mobx-react';

import { withNamespaces, WithNamespaces } from 'react-i18next';
import {
  DriveTable,
  DriveTableDataSource,
  LastContributionViewProps,
} from '@wikifactory/voxel-ui';

import { routerStore } from 'core';
import { clientStore } from 'apps/client/app';
import { isDesktopCompatibility } from 'hooks/useDesktopCompatible';
import { createTab, isInDesktopMode } from 'libs/gutClient/electronBridge';
import type { SyncRepoAppManager } from '../appManager';
import type { ExtendedTreeNode } from '../types';

interface CloudDriveContainerProps extends WithNamespaces {
  syncRepoAppManager: SyncRepoAppManager;
  setSideBarFile: (file: DriveTableDataSource) => void;
}

export const CloudDriveContainer = withNamespaces()(
  observer(
    ({ syncRepoAppManager, setSideBarFile }: CloudDriveContainerProps) => {
      const { latestContribution, lockingManager, repoManager } =
        syncRepoAppManager;

      // TODO: remove this
      let lastContributionView: LastContributionViewProps = null;

      if (latestContribution) {
        const handleCheckInsClick = () => {
          const historyHeader = document.getElementById('history-header');
          if (!historyHeader) return;

          window.scrollTo({
            top: historyHeader.offsetTop,
            behavior: 'smooth',
          });
        };

        lastContributionView = {
          username: latestContribution.creatorProfile.username,
          description: `${latestContribution.title} - ${latestContribution.description}`,
          contributionDate: latestContribution.dateCreated,
          numContributions:
            syncRepoAppManager.parent.parent.project?.contributions?.totalCount, // TODO we have this in the query

          avatarUrl: latestContribution.creatorProfile.avatar?.url,

          onCheckInsClick: () => {
            handleCheckInsClick();
          },
        };
      }

      const nodesToDataSource = (
        node: ExtendedTreeNode,
      ): DriveTableDataSource => {
        const syncStatus = 'none';
        const progress: number = 0;

        const repoFile =
          repoManager.contributionUpstreamFilesByFilename[node.path];
        const cwdFile = repoManager.repoCwdFilesByFilename[node.path];
        const lastContributionMessage = cwdFile?.reference.origin.title || '';

        const lockedBy = lockingManager.lockedFilesMap[node.fullPath];
        const fileLocked = !!lockedBy;

        let dataSource = {
          id: node.fullPath,
          filename: node.path,
          fileType: 'file',
          lastContributionMessage,
          lastModified: repoFile?.lastUpdated,
          selection: node.selection,
          progress,
          syncStatus,
          lockedBy: lockedBy || '—',
          fileLocked,
          isRemoteLock: !!lockingManager.remoteLockedPathsMap[node.fullPath],
          revision: repoFile?.repoRevision,
          // not being rendered
          maturity: 'maturity',
        } as DriveTableDataSource;

        if (node.tree) {
          const innerRemoteLockedPath = lockingManager.remoteLockedPaths.find(
            path => path.startsWith(node.fullPath),
          );
          const isFolderRemoteLocked = Boolean(innerRemoteLockedPath);
          const folderLocked =
            isFolderRemoteLocked ||
            lockingManager.lockedPaths.some(path =>
              path.startsWith(node.fullPath),
            );
          const folderLockedBy =
            lockingManager.lockedFilesMap[innerRemoteLockedPath];

          dataSource = {
            ...dataSource,
            fileType: 'folder',
            children: node.tree.map(nodesToDataSource),
            fileLocked: folderLocked,
            isRemoteLock: isFolderRemoteLocked,
            lockedBy: folderLockedBy || '—',
          };
        }
        return dataSource;
      };

      const dataSource =
        syncRepoAppManager.remoteCurrentPathContents.map(nodesToDataSource);

      const navigateToDirectory = (path: string) => {
        const params = {
          ...routerStore.route.params,
          basepath: path || null,
        };

        const routeName = routerStore.route.name;

        const directoryRouteName = routeName.endsWith('.directory')
          ? routeName
          : `${routeName}.directory`;

        const rootDirectoryRouteName = directoryRouteName.substring(
          0,
          directoryRouteName.length - '.directory'.length,
        );

        const toRouteName = params.basepath
          ? directoryRouteName
          : rootDirectoryRouteName;
        routerStore.navigate(toRouteName, params);
      };

      const navigateToFile = async (path: string, filename: string) => {
        const params = {
          ...routerStore.route.params,
          basepath: path || null,
        };

        const toRouteName = `${clientStore.spaceRoute.route}.file`;

        const compatibleWithTab = await isDesktopCompatibility({
          feature: 'tab',
        });

        if (isInDesktopMode() && compatibleWithTab) {
          const dot = path.includes('/') ? '/...' : '';

          createTab(
            `${window.location.href}/${path}`,
            `${
              syncRepoAppManager?.parent?.parent?.project?.name || ''
            } ${dot} / ${filename}`,
            'project-file',
            {
              routeName: toRouteName,
              options: params,
            },
          );

          return;
        }

        routerStore.navigate(toRouteName, params);
      };

      const handleBreadcrumbClick = (name: string, index: number) => {
        const path = syncRepoAppManager.breadcrumbs
          .slice(1)
          .slice(0, index)
          .join('/');

        navigateToDirectory(path);
      };

      const handleFileNameClick = (file: DriveTableDataSource) => {
        const { basepath } = routerStore.route.params;
        if (file.fileType === 'folder') {
          let path = file.filename;
          if (basepath) path = `${basepath}/${file.filename}`;
          navigateToDirectory(path);
        } else {
          let path = file.filename;
          if (basepath) path = `${basepath}/${file.filename}`;
          navigateToFile(path, file.filename);
        }
      };

      const shouldRenderBreadcrumbs =
        syncRepoAppManager.breadcrumbs?.length > 1;
      const breadcrumbs = shouldRenderBreadcrumbs
        ? syncRepoAppManager.breadcrumbs
        : null;

      const loading =
        !syncRepoAppManager.remoteHeadCommit &&
        syncRepoAppManager.remoteTreeQuery?.loading;
      return (
        <>
          <DriveTable
            title='Cloud Drive'
            loading={loading}
            dataSource={dataSource}
            hasFileDetailsButton
            onFileDetailsClick={(file: DriveTableDataSource) => {
              setSideBarFile(file);
            }}
            lastContributionView={lastContributionView}
            toolBar={{
              breadcrumbs,
              onBreadcrumbClick: handleBreadcrumbClick,
              selectedNum: syncRepoAppManager.remoteTableSelectedRowIds.length,
            }}
            onFileNameClick={handleFileNameClick}
            onSelect={syncRepoAppManager.SelectRemoteRow}
          />
        </>
      );
    },
  ),
);
